import { useCallback, useEffect, useState } from "react";
import { initVolume } from "../audio/sound";
import { caseIdFromUrl, DEFAULT_CASE_ID, resolveCase } from "../case/registry";
import { modulesFor } from "../modules/registry";
import { getKV, setKV } from "../persistence/db";
import { applySave, loadAutosave, startAutosave } from "../persistence/saves";
import { useGameStore } from "../state/gameStore";
import { Boot } from "./Boot";
import { Desktop } from "./Desktop";
import { Login } from "./Login";
import { ModulesInstall } from "./ModulesInstall";

/**
 * Orquestador de la shell (os §1): boot → login → desktop.
 * El boot es skippeable con clic/tecla solo a partir de la segunda vez
 * (flag `bootSeen` en Dexie). El nombre del detective persiste, igual que el
 * último expediente elegido (la URL `?exp=` tiene prioridad).
 */

type Phase = "boot" | "login" | "installing" | "desktop";

export function Shell() {
  const [phase, setPhase] = useState<Phase>("boot");
  const [bootSkippable, setBootSkippable] = useState(false);
  const [savedName, setSavedName] = useState<string | null>(null);
  const [caseId, setCaseId] = useState<string>(DEFAULT_CASE_ID);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    void initVolume();
    Promise.all([
      getKV<boolean>("bootSeen"),
      getKV<string>("detectiveName"),
      getKV<string>("lastCaseId"),
    ]).then(([seen, name, lastCase]) => {
      if (!alive) return;
      setBootSkippable(seen === true);
      setSavedName(name ?? null);
      setCaseId(caseIdFromUrl() ?? resolveCase(lastCase).id);
      setReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const onBootDone = useCallback(() => {
    void setKV("bootSeen", true);
    setPhase("login");
  }, []);

  const onLogin = useCallback((name: string, chosenCaseId: string) => {
    void (async () => {
      await setKV("detectiveName", name);
      await setKV("lastCaseId", chosenCaseId);
      setSavedName(name);
      setCaseId(chosenCaseId);
      // "Montar el expediente": el caso entra al dominio antes de pisar el
      // escritorio. Si hay autosave del caso, cargar es replay del log (T15).
      const kase = resolveCase(chosenCaseId);
      useGameStore.getState().loadCase(kase);
      const saved = await loadAutosave(kase.id);
      if (saved) applySave(saved);
      startAutosave(name);
      // F1-T07: instalación diegética de módulos ("La Agencia habilitó: …")
      setPhase(modulesFor(kase).length > 0 ? "installing" : "desktop");
    })();
  }, []);

  if (!ready) return null; // un frame en negro mientras Dexie responde

  if (phase === "boot") return <Boot skippable={bootSkippable} onDone={onBootDone} />;
  if (phase === "login")
    return <Login initialName={savedName ?? ""} initialCaseId={caseId} onLogin={onLogin} />;
  if (phase === "installing")
    return <ModulesInstall caseId={caseId} onDone={() => setPhase("desktop")} />;
  return <Desktop detectiveName={savedName ?? "Verificador"} />;
}
